import React, { ReactElement, createContext, useContext, useEffect, useState } from 'react'
import { MapContext } from '../../App/MapContextContainer'
import Point from '@arcgis/core/geometry/Point'
import { PREFERRED_WKID } from '../CustomAreas/UserDataContextContainer'
import errors from '../../App/errors'
import { PageContext } from '../../App/PageContextContainer'

type Props = {
    children: ReactElement
}

export interface INaturalForestWithClashData {
    geometry: {
        coordinates: number[]
    },
    properties: {
        name: string,
        status: number
    }
}

interface IFeatures {
    features: INaturalForestWithClashData[]
}

interface INaturalForestsContext {
    naturalForestsWithClashData?: IFeatures,
    selectedNaturalForest?: string,
    onNaturalForestSelected: (name: string) => void,
    totalNaturalForestsSizeHa?: number,
    naturalForestsSizeUnderThreatHa?: number
}

export const NaturalForestsContext = createContext({} as INaturalForestsContext);

export default function NaturalForestsContextContainer(props: Props) {
    const [naturalForestsWithClashData, setNaturalForestsWithClashData] = useState<undefined | IFeatures>();
    const [totalNaturalForestsSizeHa, setTotalNaturalForestsSizeHa] = useState<number | undefined>();
    const [naturalForestsSizeUnderThreatHa, setNaturalForestsSizeUnderThreatHa] = useState<number | undefined>();
    const mapContext = useContext(MapContext);
    const pageContext = useContext(PageContext);

    const onNaturalForestSelected = (name: string) => {
        const coordinates = naturalForestsWithClashData?.features.find(n => n.properties.name === name);

        const point = new Point({
            latitude: coordinates?.geometry.coordinates[1],
            longitude: coordinates?.geometry.coordinates[0],
            spatialReference: { wkid: PREFERRED_WKID }
        });

        mapContext.navigateToPoint(point);
    };

    useEffect(() => {
        const asyncCall = async () => {
            try {
                pageContext.setLoadingSomething({ translationKey: "loadingNaturalForests" });

                const response = await fetch(`${process.env.REACT_APP_API_URL}/api/GetNaturalForestsWithClashInformation`);
                const features = await response.json();

                const geojson = {
                    ...features,
                    type: "FeatureCollection",
                };

                setNaturalForestsWithClashData(geojson);

                const totalNaturalForestsSizeHa = geojson.features.reduce((acc: number, curr: any) => {
                    return acc + curr.properties.areaSizeHa;
                }, 0);

                setTotalNaturalForestsSizeHa(Math.round(totalNaturalForestsSizeHa));

                const totalSizeUnderThreatHa = geojson.features.reduce((acc: number, curr: any) => {
                    return acc + (curr.properties.intersectionSizeHa || 0);
                }, 0);

                setNaturalForestsSizeUnderThreatHa(Math.round(totalSizeUnderThreatHa));
            } catch (error) {
                pageContext.setMapError(errors.naturalForestsLoadingError);
            }
            finally {
                pageContext.resetLoadingSomething({ translationKey: "loadingNaturalForests" });
            }
        }

        asyncCall();
    }, []);

    return (
        <NaturalForestsContext.Provider value={{ naturalForestsWithClashData, onNaturalForestSelected, naturalForestsSizeUnderThreatHa, totalNaturalForestsSizeHa }}>
            {props.children}
        </NaturalForestsContext.Provider>
    )
}